import React, { useEffect, useRef, useState } from 'react';
import Perspective from 'perspectivejs';
import { Box } from '@mui/material';

function CombinedPerspectiveCanvas({ base_image, source_images = [], minHeight }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [loadedAssets, setLoadedAssets] = useState({ base: null, overlays: [] });
  const [dprBooster, setDprBooster] = useState(1.25);

  // 1. Load the base image AND the array of source images
  useEffect(() => {
    if (!base_image || source_images.length === 0) return;

    const baseImage = new Image();
    let totalImages = 1 + source_images.length;
    let loadedCount = 0;
    
    const loadedOverlays = [];

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        setLoadedAssets({
          base: baseImage,
          overlays: loadedOverlays
        });
      }
    };

    baseImage.onload = checkAllLoaded;
    baseImage.src = base_image;

    source_images.forEach((item, index) => {
      const img = new Image();

      setDprBooster(item.dprBooster);

      img.onload = () => {
        loadedOverlays[index] = {
          element: img,
          coords: item.coords,
          zIndex: item.zIndex !== undefined ? item.zIndex : 1
        };
        checkAllLoaded();
      };
      img.src = item.url;
    });

  }, [base_image, source_images]);

  // 2. Render Loop
  useEffect(() => {
    if (!loadedAssets.base || !canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    // Smooth Step-Down Scaling
    const getSmoothScaledTexture = (sourceImg, targetWidth, targetHeight) => {
      if (targetWidth <= 0 || targetHeight <= 0) return sourceImg;
      
      const offscreenCanvas = document.createElement('canvas');
      const offCtx = offscreenCanvas.getContext('2d');
      
      let currentWidth = sourceImg.width;
      let currentHeight = sourceImg.height;
      
      offscreenCanvas.width = currentWidth;
      offscreenCanvas.height = currentHeight;
      
      offCtx.imageSmoothingEnabled = true;
      offCtx.imageSmoothingQuality = 'high';
      offCtx.drawImage(sourceImg, 0, 0);

      while (currentWidth * 0.5 > targetWidth && currentHeight * 0.5 > targetHeight) {
        currentWidth = Math.round(currentWidth * 0.5);
        currentHeight = Math.round(currentHeight * 0.5);
        
        const stepCanvas = document.createElement('canvas');
        stepCanvas.width = currentWidth;
        stepCanvas.height = currentHeight;
        const stepCtx = stepCanvas.getContext('2d');
        
        stepCtx.imageSmoothingEnabled = true;
        stepCtx.imageSmoothingQuality = 'high';
        stepCtx.drawImage(offscreenCanvas, 0, 0, offscreenCanvas.width, offscreenCanvas.height, 0, 0, currentWidth, currentHeight);
        
        offscreenCanvas.width = currentWidth;
        offscreenCanvas.height = currentHeight;
        offCtx.drawImage(stepCanvas, 0, 0);
      }
      
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = targetWidth;
      finalCanvas.height = targetHeight;
      const finalCtx = finalCanvas.getContext('2d');
      
      finalCtx.imageSmoothingEnabled = true;
      finalCtx.imageSmoothingQuality = 'high';
      finalCtx.drawImage(offscreenCanvas, 0, 0, currentWidth, currentHeight, 0, 0, targetWidth, targetHeight);
      
      return finalCanvas;
    };

    const render = () => {
      animationFrameId = requestAnimationFrame(() => {
        if (!containerRef.current || !canvasRef.current) return;

        const dpr = window.devicePixelRatio || 1;

        const displayHeight = containerRef.current.clientHeight;
        const inverseAspectRatio = loadedAssets.base.width / loadedAssets.base.height;
        const displayWidth = displayHeight * inverseAspectRatio;

        // Keep internal resolution high for baseline rendering data
        const qualityBooster = dpr * dprBooster;
        const internalWidth = displayWidth * qualityBooster;
        const internalHeight = displayHeight * qualityBooster;

        if (canvas.width !== internalWidth || canvas.height !== internalHeight) {
          canvas.width = internalWidth;
          canvas.height = internalHeight;
          
          canvas.style.width = `${displayWidth}px`;
          canvas.style.height = `${displayHeight}px`;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, internalWidth, internalHeight);

        const drawLayer = (overlay) => {
          if (!overlay) return;

          const xs = overlay.coords.map(c => c[0] * internalWidth);
          const ys = overlay.coords.map(c => c[1] * internalHeight);
          const estimatedTargetWidth = Math.max(...xs) - Math.min(...xs);
          const estimatedTargetHeight = Math.max(...ys) - Math.min(...ys);

          const targetW = Math.round(Math.max(estimatedTargetWidth, 200));
          const targetH = Math.round(Math.max(estimatedTargetHeight, 200));

          const optimizedTexture = getSmoothScaledTexture(overlay.element, targetW, targetH);

          const p = new Perspective(ctx, optimizedTexture);
          
          const mappedCoords = overlay.coords.map(([pctX, pctY]) => [
            internalWidth * pctX,
            internalHeight * pctY
          ]);
          p.draw(mappedCoords);
        };

        const backgroundOverlays = loadedAssets.overlays
          .filter(item => item && item.zIndex < 0)
          .sort((a, b) => a.zIndex - b.zIndex);

        const foregroundOverlays = loadedAssets.overlays
          .filter(item => item && item.zIndex >= 0)
          .sort((a, b) => a.zIndex - b.zIndex);

        backgroundOverlays.forEach(drawLayer);
        ctx.drawImage(loadedAssets.base, 0, 0, internalWidth, internalHeight);
        foregroundOverlays.forEach(drawLayer);
      });
    };

    render();

    const resizeObserver = new ResizeObserver(() => render());
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, [loadedAssets]);

  return (
    <Box
      ref={containerRef}
      style={{
        display: 'block',
        height: '100%',
        maxHeight: '100%',
        minHeight: minHeight,
        position: 'relative',
        zIndex: 1
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          minHeight: minHeight,
          // COLOR-SAFE SHARPENING:
          // Dropped contrast from 1.25 to 1.05 so light grays (#eee) stay distinct from white (#fff).
          // We pull brightness down slightly to deepen black text without clipping highlights.
          filter: 'contrast(1.05) brightness(0.96) saturate(1.0)',
          imageRendering: '-webkit-optimize-contrast',
        }}
      />
    </Box>
  );
}

export default CombinedPerspectiveCanvas;